import { useState } from 'react';
import { FIRST_MOVES, MONS } from '../mock/data.ts';
import type { LeadPair, LeadPrediction, SpeciesId } from '../types.ts';
import { CitationBadge } from './CitationBadge.tsx';
import { Sprite } from './Sprite.tsx';

// Top-3 most-likely opponent lead pairs from predict_opponent_leads. Expand
// a row to see what each lead typically DOES on turn 1 (from
// get_species_first_moves). Citation: PS! Replays.
export function LeadPredictionCard({
  data,
  defaultExpandedIdx = 0,
}: {
  data: LeadPrediction;
  defaultExpandedIdx?: number | null;
}) {
  const [expanded, setExpanded] = useState<number | null>(defaultExpandedIdx);

  // Defensive: bail rather than crash the chat tree if the tool result
  // omits the array. Same pattern as DamageCalcCard / EvSpreadCard.
  if (!data || !Array.isArray(data.predictions)) {
    return (
      <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11, opacity: 0.6 }}>
        lead prediction · missing fields
      </div>
    );
  }
  if (data.predictions.length === 0) {
    return (
      <div className="mono" style={{ color: 'var(--fg-3)', fontSize: 12, padding: '8px 10px' }}>
        no comparable lead matchups in the corpus
      </div>
    );
  }

  const lowSignal = data.matchups_used <= 3;

  return (
    <div className="vb-card">
      <div className="vb-card-header">
        <span
          className="caps"
          style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.08em' }}
        >
          opponent likely leads
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
            {data.matchups_used} matchups · {data.exact_matchups} exact
          </span>
          <CitationBadge source="pokemon-showdown" />
        </div>
      </div>

      {lowSignal && (
        <div
          style={{
            padding: '6px 12px',
            borderBottom: '1px solid var(--line-1)',
            fontSize: 11,
            color: 'var(--warn)',
            background: 'rgba(201, 168, 90, 0.08)',
          }}
        >
          Low signal: only {data.matchups_used} similar matchups in corpus.
        </div>
      )}

      <div>
        {data.predictions.map((p, i) => (
          <LeadRow
            key={`${p.lead_pair[0]}-${p.lead_pair[1]}`}
            pair={p}
            expanded={expanded === i}
            onClick={() => setExpanded(expanded === i ? null : i)}
            last={i === data.predictions.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

function LeadRow({
  pair,
  expanded,
  onClick,
  last,
}: {
  pair: LeadPair;
  expanded: boolean;
  onClick: () => void;
  last: boolean;
}) {
  return (
    <div style={{ borderBottom: last ? 'none' : '1px solid var(--line-1)' }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 12px',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          <Sprite species={pair.lead_pair[0] as SpeciesId} size={40} />
          <Sprite species={pair.lead_pair[1] as SpeciesId} size={40} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--fg-1)', fontWeight: 500, fontSize: 13 }}>
              {MONS[pair.lead_pair[0]]?.name ?? pair.lead_pair[0]}{' '}
              <span style={{ color: 'var(--fg-3)' }}>+</span>{' '}
              {MONS[pair.lead_pair[1]]?.name ?? pair.lead_pair[1]}
            </span>
            {pair.match_kind === 'exact' ? (
              <span
                className="caps mono"
                style={{
                  fontSize: 9,
                  color: 'var(--pos)',
                  background: 'rgba(136,176,122,0.10)',
                  border: '1px solid rgba(136,176,122,0.30)',
                  borderRadius: 2,
                  padding: '1px 5px',
                  letterSpacing: '0.08em',
                }}
              >
                exact
              </span>
            ) : pair.match_kind === 'fuzzy' ? (
              <span
                className="caps mono"
                style={{
                  fontSize: 9,
                  color: 'var(--warn)',
                  background: 'rgba(201,168,90,0.10)',
                  border: '1px solid rgba(201,168,90,0.30)',
                  borderRadius: 2,
                  padding: '1px 5px',
                  letterSpacing: '0.08em',
                }}
              >
                fuzzy
              </span>
            ) : null}
          </div>
          <div
            className="mono tabular"
            style={{ color: 'var(--fg-3)', fontSize: 11, marginTop: 2 }}
          >
            {pair.count} of {pair.count / pair.fraction_of_matchups || 0} ·{' '}
            {(pair.fraction_of_matchups * 100).toFixed(0)}%
          </div>
        </div>
        <span style={{ color: 'var(--fg-4)', fontSize: 14 }}>{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && <LeadFirstMoves pair={pair.lead_pair} />}
    </div>
  );
}

function LeadFirstMoves({ pair }: { pair: [string, string] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
        padding: '8px 12px 12px',
        borderTop: '1px dashed var(--line-1)',
        background: 'var(--bg-2)',
      }}
    >
      {pair.map((species) => (
        <FirstMovesPanel key={species} species={species as SpeciesId} />
      ))}
    </div>
  );
}

function FirstMovesPanel({ species }: { species: SpeciesId }) {
  const data = FIRST_MOVES[species];
  return (
    <div>
      <div
        className="caps mono"
        style={{
          color: 'var(--fg-3)',
          fontSize: 9,
          letterSpacing: '0.08em',
          marginBottom: 4,
        }}
      >
        {MONS[species]?.name ?? species} · turn 1
      </div>
      {!data ? (
        <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11 }}>
          no first-move data
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.results.slice(0, 4).map((m) => (
            <div
              key={m.move}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                fontSize: 12,
              }}
            >
              <span style={{ color: m.move === '(no_move)' ? 'var(--fg-4)' : 'var(--fg-1)' }}>
                {m.move === '(no_move)' ? '— no move' : m.move}
              </span>
              <span className="mono tabular" style={{ color: 'var(--fg-3)' }}>
                {(m.fraction * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
